/**
 * 字幕（テロップ）共通ユーティリティ
 *
 * 作品に登録する字幕テキストは、文字起こしモーダルの「全文コピー」と同じ
 * 行フォーマットを想定する:
 *
 *   [M:SS] テキスト
 *   [M:SS.FF] テキスト   （フレーム付きも許容。FF は fps 未満のフレーム番号）
 *
 * - タグの無い行は直前のキューの続き（改行）として扱う
 * - テキストが空のタグ行（例 `[1:30]`）は「ここで字幕を消す」クリアマーカー
 * - 行順が前後していても開始秒でソートして扱う
 */
import { DEFAULT_FPS } from './timecode';

export interface SubtitleCue {
  /** 表示開始秒 */
  start: number;
  /** 表示終了秒（= 次のキューの開始秒。最後のキューは null = メディア末尾まで） */
  end: number | null;
  /** 表示テキスト（空文字はクリアマーカー = 何も表示しない区間） */
  text: string;
}

/** 行頭の [M:SS] / [M:SS.FF] タグ（分は 1〜3 桁を許容。総分数表記に対応） */
const LINE_TAG_RE = /^\s*\[(\d{1,3}):(\d{2})(?:\.(\d{1,2}))?\]\s*(.*)$/;

/** 字幕テキストをキュー配列にパースする。タグ行が 1 つも無ければ空配列。 */
export function parseSubtitleCues(text: string | null | undefined): SubtitleCue[] {
  if (!text) return [];
  const draft: { start: number; text: string }[] = [];
  for (const line of text.split(/\r?\n/)) {
    const match = line.match(LINE_TAG_RE);
    if (match) {
      const frames = match[3] !== undefined ? parseInt(match[3], 10) / DEFAULT_FPS : 0;
      draft.push({
        start: parseInt(match[1], 10) * 60 + parseInt(match[2], 10) + frames,
        text: match[4].trim(),
      });
    } else if (draft.length > 0 && line.trim()) {
      // タグなし行は直前キューへの追記（複数行字幕）
      draft[draft.length - 1].text += `\n${line.trim()}`;
    }
  }
  draft.sort((a, b) => a.start - b.start);
  return draft.map((cue, i) => ({
    start: cue.start,
    end: i < draft.length - 1 ? draft[i + 1].start : null,
    text: cue.text,
  }));
}

/**
 * 再生位置に対応するキューの index を返す（該当なしは -1）。
 * キューは parseSubtitleCues の返り値（開始秒昇順）を渡すこと。
 */
export function findActiveCueIndex(cues: SubtitleCue[], time: number): number {
  for (let i = cues.length - 1; i >= 0; i--) {
    if (time >= cues[i].start) return i;
  }
  return -1;
}
